import { Link } from 'react-router-dom'
import { Button } from '@gjl/ui'
import { SEO } from '@/components/SEO'
import { GlassCard } from '@/components/ui/glass-card'
import { CaseStudies } from '@/components/CaseStudies'
import { useLuxeHero, useDirectionalReveal, useSectionSequence, useParallax } from '@/hooks'
import { ArrowRight, Bot, Sparkles, Network, ShieldCheck, Workflow, GaugeCircle, Brain } from 'lucide-react'

const capabilities = [
  {
    icon: Network,
    title: 'Multi-agent orchestration',
    description:
      'Fleets of LLM agents organized as sensors, synthesizers, and dispatchers — running unattended on serverless infra.',
  },
  {
    icon: ShieldCheck,
    title: 'LLM safety & grounding gates',
    description:
      'Evidence-auditor gates and surgical-repair loops so models never ship an unsupported claim. Fail closed, hold for review.',
  },
  {
    icon: Brain,
    title: 'Context engineering',
    description:
      'Single-source domain contracts that compose the agent prompt, the tooling, and the skills — so rules never drift.',
  },
  {
    icon: Workflow,
    title: 'Human-in-the-loop tool agents',
    description:
      'Agents with large tool surfaces that propose writes and commit only after a human approves. Operate, don’t override.',
  },
  {
    icon: GaugeCircle,
    title: 'Automated evaluation',
    description:
      'Rubric-driven scoring engines that grade real artifacts — including browser-rendered checks static tools can’t see.',
  },
  {
    icon: Bot,
    title: 'Bedrock production infra',
    description:
      'Claude on AWS Bedrock, Lambda, DynamoDB, SES, Terraform — model-tiered for cost, deployed through CI/CD.',
  },
]

export function Home() {
  const heroRef = useLuxeHero<HTMLDivElement>(0.3)
  const bgRef = useParallax<HTMLDivElement>(0.3)

  const aboutRef = useDirectionalReveal<HTMLElement>('.reveal', 'left', {
    stagger: 0.15,
    blur: 6,
  })

  const capsRef = useSectionSequence<HTMLElement>([
    { selector: 'h2', direction: 'right', delay: 0 },
    { selector: '.subtitle', direction: 'right', delay: 0.1 },
    { selector: '.skill-card', direction: 'bottom', stagger: 0.07, delay: 0.2 },
  ])

  const demoRef = useDirectionalReveal<HTMLElement>('.reveal', 'fade', {
    scale: 0.95,
    blur: 8,
  })

  const contactRef = useDirectionalReveal<HTMLElement>('.reveal', 'fade', {
    scale: 0.9,
    blur: 8,
    duration: 1.0,
  })

  return (
    <>
      <SEO
        title="AI Engineer — Production LLM Systems"
        description="Garrett John Law builds production LLM systems on AWS Bedrock — retrieval that stays grounded, agents that stay in bounds, and the eval, observability, and cost work that keeps them shippable."
        url="https://software.garrettjohnlaw.com/"
      />

      {/* Hero */}
      <section className="relative flex min-h-[100vh] items-center justify-center overflow-hidden">
        <div ref={bgRef} className="absolute inset-0">
          <img
            src="/servers.jpg"
            alt="Production infrastructure"
            className="absolute inset-0 h-full w-full scale-110 object-cover object-center"
          />
          <div className="hero-overlay absolute inset-0" />
          <div className="bg-gradient-radial-cyber absolute inset-0" />
        </div>

        <div ref={heroRef} className="relative z-10 mx-auto max-w-4xl px-4 text-center">
          <p className="text-primary mb-6 font-mono text-sm font-medium tracking-wide break-words sm:text-lg">
            &lt;retrieval · agents · evals · cost&gt;
          </p>
          <h1 className="text-fluid-h1 text-foreground mb-6">Garrett John Law</h1>
          <p className="text-fluid-body-lg text-foreground mx-auto mb-4 max-w-2xl">
            I build LLM systems that survive contact with production.
          </p>
          <p className="text-muted-foreground mx-auto mb-4 max-w-xl text-base">
            Retrieval that stays grounded, agents that stay in bounds — and the eval harnesses, tracing, and cost
            controls that make them shippable rather than demoable.
          </p>
          <p className="text-foreground/85 mx-auto mb-8 max-w-xl text-sm">
            Three of them are running right now. These systems run in production; the numbers trace to the source and
            runtime config behind them.
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Button
              asChild
              className="gradient-cyber text-primary-foreground hover:glow-cyber-sm px-8 py-3 text-lg font-semibold transition-shadow"
            >
              <a href="#case-studies">
                See the systems
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-border hover:border-primary hover:text-primary px-8 py-3 text-lg"
            >
              <Link to="/resume">View Résumé</Link>
            </Button>
          </div>

          {/* Live demo hint */}
          <div className="border-primary/30 bg-primary/5 text-foreground/90 mt-10 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm">
            <Sparkles className="text-primary h-4 w-4" />
            Ask the assistant in the corner anything about this work
          </div>
        </div>

        <div aria-hidden="true" className="absolute bottom-8 left-8 hidden opacity-70 lg:block">
          <pre className="code-block p-4 text-xs text-cyan-300">
            <code>{`const engineer = {
  builds: "AI agents",
  guards: "grounding gates",
  ships: "to production"
};`}</code>
          </pre>
        </div>
      </section>

      <div className="section-divider mx-auto max-w-4xl" />

      {/* About — AI-native */}
      <section ref={aboutRef} className="px-4 py-20 md:py-28">
        <div className="mx-auto max-w-5xl">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <div className="space-y-6">
              <h2 className="reveal text-fluid-h2 text-foreground">On the defended side of the AI line</h2>
              <p className="reveal text-muted-foreground text-lg">
                Generic application work is commoditizing fast, while agent and LLM-infrastructure work is where demand
                is heading. These systems are how I stay on the right side of that shift — I do the work, not just claim
                it.
              </p>
              <p className="reveal text-muted-foreground text-lg">
                At Re:Build Manufacturing I build AI infrastructure (Terraform, observability, RAG). On my own time I
                run a sales pipeline of ~15 LLM agents (plus a few supporting Lambdas), a 135-tool operations agent, and
                an automated website-evaluation engine — all in production, all on Bedrock.
              </p>
              <div className="reveal flex gap-4 pt-2">
                <Button asChild>
                  <Link to="/about">
                    More about the work
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
            <div className="reveal">
              <GlassCard accent className="p-8">
                <div className="space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="gradient-cyber flex h-12 w-12 items-center justify-center rounded-xl">
                      <Network className="text-primary-foreground h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-foreground text-3xl font-bold">~15</p>
                      <p className="text-muted-foreground text-sm">Autonomous agents in production</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="gradient-cyber flex h-12 w-12 items-center justify-center rounded-xl">
                      <Bot className="text-primary-foreground h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-foreground text-3xl font-bold">135</p>
                      <p className="text-muted-foreground text-sm">Tools in one agent’s registry</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="gradient-cyber flex h-12 w-12 items-center justify-center rounded-xl">
                      <GaugeCircle className="text-primary-foreground h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-foreground text-3xl font-bold">$100→$50</p>
                      <p className="text-muted-foreground text-sm">Monthly Bedrock cap cut (spend roughly halved)</p>
                    </div>
                  </div>
                </div>
              </GlassCard>
            </div>
          </div>
        </div>
      </section>

      <div className="section-divider mx-auto max-w-4xl" />

      {/* Case studies */}
      <div id="case-studies" className="scroll-mt-24">
        <CaseStudies />
      </div>

      <div className="section-divider mx-auto max-w-4xl" />

      {/* Capabilities */}
      <section ref={capsRef} className="px-4 py-20 md:py-28">
        <div className="mx-auto max-w-5xl">
          <div className="mb-12 text-center">
            <h2 className="text-fluid-h2 text-foreground mb-4">What I build with</h2>
            <p className="subtitle text-muted-foreground mx-auto max-w-2xl text-lg">
              The capabilities behind the systems above — the work AI can’t do for itself.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {capabilities.map((cap) => (
              <GlassCard key={cap.title} hover className="skill-card">
                <div className="space-y-3">
                  <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-lg">
                    <cap.icon className="text-primary h-5 w-5" />
                  </div>
                  <p className="text-foreground font-semibold">{cap.title}</p>
                  <p className="text-muted-foreground text-sm">{cap.description}</p>
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider mx-auto max-w-4xl" />

      {/* Live AI demo callout */}
      <section ref={demoRef} className="px-4 py-20 md:py-28">
        <div className="mx-auto max-w-4xl">
          <GlassCard accent className="reveal py-12 text-center md:py-16">
            <div className="gradient-cyber mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl">
              <Bot className="text-primary-foreground h-7 w-7" />
            </div>
            <h2 className="text-fluid-h2 text-foreground mb-4">This site ships a grounded assistant</h2>
            <p className="text-muted-foreground mx-auto mb-6 max-w-2xl text-lg">
              The assistant in the bottom corner does keyword retrieval over a verified fact corpus — the same grounding
              discipline I’d feed a Bedrock model (answer from source, or say you don’t know). It’s a demo, not a live
              model call.
            </p>
            <p className="text-muted-foreground/80 text-sm">
              Open it and ask how the Bedrock cap was cut, or what the doctrine system does.
            </p>
          </GlassCard>
        </div>
      </section>

      <div className="section-divider mx-auto max-w-4xl" />

      {/* Contact CTA */}
      <section ref={contactRef} className="px-4 py-20 md:py-28">
        <div className="mx-auto max-w-4xl">
          <GlassCard accent className="reveal py-12 text-center md:py-16">
            <h2 className="text-fluid-h2 text-foreground mb-4">Building with AI? Let’s talk.</h2>
            <p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-lg">
              I’m open to AI/agent engineering and AI-infrastructure roles, and to consulting through Fugue Lab. If you
              need someone who ships AI systems that actually run, reach out.
            </p>
            <Button
              asChild
              className="gradient-cyber text-primary-foreground hover:glow-cyber-sm animate-pulse-glow px-8 py-3 text-lg font-semibold"
            >
              <Link to="/contact">
                Get in touch
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </GlassCard>
        </div>
      </section>
    </>
  )
}
